import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  updateRole: (id, role) => api.put(`/users/${id}/role?role=${role}`),
  getByDepartment: (dept) => api.get(`/users/department/${dept}`),
};

// Courses API
export const coursesAPI = {
  getAll: (params) => api.get('/courses', { params }),
  getAllAdmin: () => api.get('/courses/all'),
  getById: (id) => api.get(`/courses/${id}`),
  create: (data) => api.post('/courses', data),
  update: (id, data) => api.put(`/courses/${id}`, data),
  delete: (id) => api.delete(`/courses/${id}`),
  publish: (id) => api.put(`/courses/${id}/publish`),
};

// Modules API
export const modulesAPI = {
  getByCourse: (courseId) => api.get(`/modules/course/${courseId}`),
  getById: (id) => api.get(`/modules/${id}`),
  create: (data) => api.post('/modules', data),
  update: (id, data) => api.put(`/modules/${id}`, data),
  delete: (id) => api.delete(`/modules/${id}`),
};

// Quizzes API
export const quizzesAPI = {
  getById: (id) => api.get(`/quizzes/${id}`),
  getByModule: (moduleId) => api.get(`/quizzes/module/${moduleId}`),
  create: (data) => api.post('/quizzes', data),
  submit: (quizId, answers) => api.post(`/quizzes/${quizId}/attempt`, { quiz_id: quizId, answers }),
  getResults: (quizId) => api.get(`/quizzes/${quizId}/results`),
};

// Enrollments API
export const enrollmentsAPI = {
  getMy: () => api.get('/enrollments/my'),
  getById: (id) => api.get(`/enrollments/${id}`),
  enroll: (courseId) => api.post('/enrollments', { course_id: courseId }),
  completeModule: (enrollmentId, moduleId) => 
    api.put(`/enrollments/${enrollmentId}/complete-module/${moduleId}`),
};

// Certificates API
export const certificatesAPI = {
  getMy: () => api.get('/certificates/my'),
  getById: (id) => api.get(`/certificates/${id}`),
};

// Badges API
export const badgesAPI = {
  getAll: () => api.get('/badges'),
  getMy: () => api.get('/badges/my'),
};

// Learning Paths API
export const learningPathsAPI = {
  getAll: (department) => api.get('/learning-paths', { params: { department } }),
  getById: (id) => api.get(`/learning-paths/${id}`),
  create: (data) => api.post('/learning-paths', data),
};

// Discussions API
export const discussionsAPI = {
  getByCourse: (courseId) => api.get(`/discussions/course/${courseId}`),
  create: (data) => api.post('/discussions', data),
  addReply: (discussionId, content) => 
    api.post(`/discussions/${discussionId}/reply`, { discussion_id: discussionId, content }),
};

// Analytics API
export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getUser: (userId) => api.get(`/analytics/user/${userId}`),
  getDepartment: (dept) => api.get(`/analytics/department/${dept}`),
};

// Leaderboard API
export const leaderboardAPI = {
  get: (department, limit = 20) => 
    api.get('/leaderboard', { params: { department, limit } }),
};

// Recommendations API
export const recommendationsAPI = {
  getMy: () => api.get('/recommendations/my'),
  assign: (data) => api.post('/recommendations/assign', data),
};

// AI API
export const aiAPI = {
  generateQuiz: (data) => api.post('/ai/generate-quiz', data),
  getRecommendations: (userId) => api.get(`/ai/recommend-courses/${userId}`),
  generateLesson: (data) => api.post('/ai/generate-lesson', data),
  createCourseFromLesson: (topic, difficulty) => 
    api.post(`/ai/create-course-from-lesson?topic=${encodeURIComponent(topic)}&difficulty=${difficulty}`),
};

// Module Content API (AI-generated content with TTS)
export const moduleContentAPI = {
  generateContent: (moduleId, regenerate = false) => 
    api.post(`/modules/${moduleId}/generate-content?regenerate=${regenerate}`),
  generateContentAsync: (moduleId, regenerate = false) => 
    api.post(`/modules/${moduleId}/generate-content-async?regenerate=${regenerate}`),
  getGenerationStatus: (moduleId) => 
    api.get(`/modules/${moduleId}/generation-status`),
  getContent: (moduleId) => api.get(`/modules/${moduleId}/content`),
  generateAllCourseContent: (courseId) => api.post(`/courses/${courseId}/generate-all-content`),
  getVoices: () => api.get('/tts/voices'),
};

// Catalog API
export const catalogAPI = {
  seed: (count, category) => api.post('/catalog/seed', { count, category }),
  bulkSeed: (targetTotal = 10000, batchSize = 500) => 
    api.post('/catalog/bulk-seed', { target_total: targetTotal, batch_size: batchSize }),
  getStats: () => api.get('/catalog/stats'),
  search: (params) => api.get('/catalog/search', { params }),
};

export default api;
